import { Drawer as MuiDrawer, useMediaQuery, useTheme } from '@mui/material';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

import {
  BANNER_HEIGHT,
  COMPANY_SWITCHER_WIDTH,
  DRAWER_WIDTH,
  SIDEBAR_DEFAULT_PX,
} from 'src/constants/uiConsts';
import {
  SidebarTheme,
  SidebarThemeContext,
  useFeatureFlags,
} from 'src/context';
import { DesktopCompanySwitcher } from 'src/features/companies/CompanySwitcher';
import { useCompanySwitcherVisibility } from 'src/features/companies/useCompanySwitcherVisibility';
import { useBannerOptions } from 'src/hooks/useBannerOptions';

const useStyles = makeStyles<{
  showBanner: boolean;
  drawerTheme: SidebarTheme;
  drawerWidth: number;
}>()((_theme, props) => ({
  mobileDrawer: {
    backgroundColor: props.drawerTheme.sidebarBackgroundColor,
    width: props.drawerWidth,
    flexShrink: 0,
  },
  desktopDrawer: {
    boxSizing: 'border-box',
    backgroundColor: props.drawerTheme.sidebarBackgroundColor,
    width: props.drawerWidth,
    marginTop: props.showBanner ? `${BANNER_HEIGHT}px` : 0,
  },
}));

export const Drawer = ({
  open,
  onClose,
  children,
  resizeable,
}: {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  resizeable: boolean;
}) => {
  const { EnableNewSidebar } = useFeatureFlags();
  const drawerWidth = EnableNewSidebar ? SIDEBAR_DEFAULT_PX : DRAWER_WIDTH;
  const companySwitcherVisibility = useCompanySwitcherVisibility();
  const bannerOptions = useBannerOptions();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const drawerTheme = React.useContext(SidebarThemeContext);
  const { classes } = useStyles({
    showBanner: Boolean(bannerOptions),
    drawerTheme,
    drawerWidth,
  });

  React.useEffect(() => {
    function handleResize() {
      if (!isMobile && open) {
        onClose();
      }
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile, open, onClose]);

  // In the resizable shell the company column is its own panel beside this
  // one (see AppShell), so only the nav renders here.
  if (resizeable) {
    return (
      <div
        style={{ backgroundColor: drawerTheme.sidebarBackgroundColor }}
        className="flex flex-col h-full overflow-y-auto min-w-0 border-0 border-r border-solid border-[rgba(0,0,0,0.1)]"
      >
        {children}
      </div>
    );
  }

  return (
    <div className="z-modal">
      <div className={isMobile ? 'block' : 'hidden'}>
        <MuiDrawer
          variant="temporary"
          classes={{
            paper: classes.mobileDrawer,
          }}
          anchor="left"
          onClose={onClose}
          open={open}
          sx={{ zIndex: theme.zIndex.modal }}
        >
          {children}
        </MuiDrawer>
      </div>
      <div className={isMobile ? 'hidden' : 'block'}>
        {companySwitcherVisibility.visibility === 'desktop' && (
          <div
            style={{
              backgroundColor: drawerTheme.sidebarBackgroundColor,
              width: COMPANY_SWITCHER_WIDTH,
              zIndex: theme.zIndex.modal + 2,
            }}
            className="box-border fixed left-0 bottom-0 top-0 border-0 border-solid border-r border-r-[rgba(0,0,0,0.1)]"
          >
            <DesktopCompanySwitcher theme={drawerTheme} />
          </div>
        )}
        <div
          style={{
            width: drawerWidth,
            left:
              companySwitcherVisibility.visibility === 'desktop'
                ? COMPANY_SWITCHER_WIDTH
                : 0,
            zIndex: theme.zIndex.modal + 2,
          }}
          className={[
            classes.desktopDrawer,
            'box-border',
            'flex',
            'flex-col',
            'fixed',
            'top-0',
            'bottom-0',
            EnableNewSidebar ? 'overflow-hidden' : 'overflow-y-auto',
            'shadow-xs',
            EnableNewSidebar
              ? 'border-r border-l-0 border-t-0 border-b-0 border-solid border-non-hover-border'
              : 'border-0',
          ].join(' ')}
        >
          {children}
        </div>
      </div>
    </div>
  );
};
